import { useEffect, useState } from 'react';
import {
  Building2, UserCheck, PauseCircle, TrendingUp, Users, Activity, MoonStar,
  ShieldCheck, ShieldAlert, HardDriveDownload,
} from 'lucide-react';
import { apiPanel } from './api';

// Métricas de la plataforma. Ninguna tarjeta decorativa: cada una responde
// a una pregunta que el operador del servicio se hace de verdad, y las dos
// que piden acción (suspendidas, dormidas) se pintan distinto cuando no
// están en cero.
export default function Metricas({ irACliente }) {
  const [m, setM] = useState(null);
  const [op, setOp] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiPanel.get('/metricas').then(setM).catch((e) => setError(e.message));
    // El estado de las tareas NO tumba la pantalla si falla: son dos
    // preguntas distintas y la de las métricas tiene que poder responderse
    // aunque la otra no.
    apiPanel.get('/operacion').then(setOp).catch(() => setOp(null));
  }, []);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!m) return <p className="text-sm text-gray-400">Cargando…</p>;

  const dinero = new Intl.NumberFormat('es-MX', {
    style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Estado del servicio</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Cifras agregadas de todos los clientes. Ningún dato de inventario.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Tarjeta icono={Building2} etiqueta="Clientes" valor={m.clientes_total}
                 nota={`${m.altas_30d} alta(s) en 30 días`} />
        <Tarjeta icono={UserCheck} etiqueta="Operando" valor={m.clientes_activos} />
        <Tarjeta
          icono={PauseCircle}
          etiqueta="Suspendidos"
          valor={m.clientes_suspendidos}
          atencion={m.clientes_suspendidos > 0}
          nota={m.clientes_suspendidos > 0 ? 'No pueden operar' : undefined}
          onClick={m.clientes_suspendidos > 0 ? () => irACliente('suspendida') : undefined}
        />
        <Tarjeta
          icono={TrendingUp}
          etiqueta="Ingreso mensual"
          valor={dinero.format(m.ingreso_mensual)}
          nota="Solo suscripciones que cobran"
        />
        <Tarjeta icono={Users} etiqueta="Usuarios activos" valor={m.usuarios_total} />
        <Tarjeta icono={Activity} etiqueta="Movimientos (30 d)" valor={m.movimientos_30d} />
        <Tarjeta
          icono={MoonStar}
          etiqueta="Sin actividad (30 d)"
          valor={m.sin_actividad_30d}
          atencion={m.sin_actividad_30d > 0}
          nota={m.sin_actividad_30d > 0 ? 'Candidatos a baja' : undefined}
        />
      </div>

      {op && <Operacion datos={op} />}

      <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
        El ingreso mensual cuenta únicamente suscripciones en estado
        <em> activa</em>. Las cuentas en prueba no suman: contarlas infla la
        cifra con dinero que nadie ha pagado.
      </p>
    </div>
  );
}

// Cómo fueron las tareas de madrugada.
//
// Existe porque el respaldo llevaba escribiendo su resultado en un archivo
// desde siempre y NADIE lo leía (deuda V-3): había que entrar al servidor
// y hacer `cat`. Un respaldo puede llevar semanas fallando en silencio, y
// nadie se entera hasta el día que hace falta restaurar.
//
// Lo mismo con la auditoría semanal de dependencias: un hallazgo que se
// queda en el journal de systemd no avisa a nadie.
//
// SIN NOTICIAS NO ES BUENA NOTICIA: una tarea atrasada o sin datos se
// pinta igual de mal que una fallida. Un temporizador apagado no produce
// errores, produce silencio.
function Operacion({ datos }) {
  const TONO = {
    OK: 'border-gray-200 bg-white text-gray-600',
    FALLO: 'border-red-300 bg-red-50 text-red-800',
    ATRASADO: 'border-amber-300 bg-amber-50 text-amber-900',
    SIN_DATOS: 'border-amber-300 bg-amber-50 text-amber-900',
    // El respaldo funciona, pero nunca sale de esta máquina. No es rojo
    // —nada está roto— y tampoco puede ser blanco: con la única copia en
    // el mismo disco que la base, un fallo de disco se lleva las dos.
    SOLO_LOCAL: 'border-amber-300 bg-amber-50 text-amber-900',
    // La tarea se hizo y salió bien, pero hay algo que corregir: por
    // ejemplo, que la copia que sale del local vaya siempre en la misma
    // memoria y la otra lleve semanas sin actualizarse.
    ATENCION: 'border-amber-300 bg-amber-50 text-amber-900',
  };
  const ICONO = { respaldo: HardDriveDownload };

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold text-gray-900">Tareas programadas</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {datos.tareas.map((t) => {
          const Icono = ICONO[t.tarea] || (t.estado === 'OK' ? ShieldCheck : ShieldAlert);
          return (
            <div key={t.tarea}
                 className={`border rounded-xl p-3 flex items-start gap-2.5 ${
                   TONO[t.estado] || TONO.SIN_DATOS}`}>
              <Icono className="h-5 w-5 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <div className="text-sm font-semibold">{t.titulo}</div>
                <div className="text-xs leading-relaxed">{t.detalle}</div>
                {t.horas != null && (
                  <div className="text-[11px] mt-0.5 opacity-80">
                    Última ejecución hace {Math.round(t.horas)} h
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Tarjeta({ icono: Icono, etiqueta, valor, nota, atencion, onClick }) {
  const Elemento = onClick ? 'button' : 'div';
  return (
    <Elemento
      onClick={onClick}
      className={`text-left bg-white border rounded-xl p-4 ${
        atencion ? 'border-amber-300 bg-amber-50/40' : 'border-gray-200'
      } ${onClick ? 'hover:border-[var(--color-marca)] cursor-pointer' : ''}`}
    >
      <div className="flex items-center gap-2 text-gray-500 mb-2">
        <Icono className={`h-4 w-4 ${atencion ? 'text-amber-600' : 'text-gray-400'}`} />
        <span className="text-xs font-semibold uppercase tracking-wide">{etiqueta}</span>
      </div>
      <div className="text-2xl font-semibold text-gray-900">{valor}</div>
      {nota && <div className="text-xs text-gray-500 mt-1">{nota}</div>}
    </Elemento>
  );
}
